import { useRef, useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import useHandleClickOutside from '../../hooks/utils/useHandleClickOutside';
import useSmartDropdown from '../../hooks/ui/useSmartDropdown';

// HELPERS
const shouldClearOnFocus = (value) => ['1', '100'].includes(String(value));

const getInitialUnit = (initialUnit, options = []) => {
    if (initialUnit?.id != null) return initialUnit;
    return options?.[0] ?? { id: 0, name: 'gram', grams: 1 };
};

const getDefaultValue = (value, unit) => {
    if (value && value > 0) return value.toString();
    return unit?.name === 'gram' ? '100' : '1';
};

const normalizeOptions = (options) => {
    if (!Array.isArray(options)) return [];

    const map = new Map();

    for (const u of options.filter(Boolean)) {
        const key = `${u.id}`;
        if (!map.has(key)) map.set(key, u);
    }

    return [...map.values()];
};

// COMPONENT
const MealItemEditor = ({
    initialValue,
    initialUnit,
    options,
    onChangeValue,
    onDelete,
    isDisabled = false,
    isCustom = false
}) => {
    // INITIAL STATE
    const editorUnit = getInitialUnit(initialUnit, options);
    const editorValue = getDefaultValue(initialValue, editorUnit);

    const [unit, setUnit] = useState(editorUnit);
    const [value, setValue] = useState(editorValue);
    const [invalidAmount, setInvalidAmount] = useState(false);

    // REFS
    const timeoutRef = useRef(null);
    const menuRef = useRef(null);

    // DROPDOWN
    const { triggerRef, dropdownRef, menuStyle, hasPosition, open, setOpen, isPartiallyHidden } =
        useSmartDropdown({ scrollContainerSelector: '.edit-meal-content' });

    useHandleClickOutside([dropdownRef, triggerRef, menuRef], open, setOpen);

    // DERIVED
    const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);

    const availableUnits = useMemo(
        () => normalizedOptions.filter((opt) => opt.id !== unit?.id),
        [normalizedOptions, unit]
    );

    const hasMultipleUnits = normalizedOptions.length > 1;

    const dropdownIsEnabled = hasMultipleUnits && !isDisabled;

    // HANDLERS
    const handleValueChange = (e) => {
        const next = e.target.value;

        setValue(next);
        onChangeValue({ value: next, unit });

        clearTimeout(timeoutRef.current);

        if (next <= 0) {
            timeoutRef.current = setTimeout(() => setInvalidAmount(true), 500);
            return;
        }

        setInvalidAmount(false);
    };

    const handleSelectUnit = (selectedUnit) => {
        setUnit(selectedUnit);
        setOpen(false);
        setInvalidAmount(false);
        onChangeValue({ value, unit: selectedUnit });
    };

    // SYNC INITIAL EDITOR VALUE
    useEffect(() => {
        if (!editorValue) return;

        onChangeValue({
            value: editorValue,
            unit: editorUnit
        });
    }, []);

    // SYNC ONLY IF PARENT VALUE DIVERGES
    useEffect(() => {
        if (isDisabled) return;

        const numericInitial = Number(initialValue);

        if (!Number.isNaN(numericInitial) && numericInitial !== Number(value)) {
            setValue(getDefaultValue(initialValue, unit));
        }

        if (initialUnit?.id !== unit?.id) setUnit(getInitialUnit(isCustom, initialUnit));
    }, [initialValue, initialUnit]);

    return (
        <motion.div
            ref={dropdownRef}
            className="meal-item-editor"
            animate={{
                opacity: isPartiallyHidden ? 0.8 : 1,
                pointerEvents: isPartiallyHidden ? 'none' : 'auto'
            }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
        >
            <input
                type="number"
                className="modal-input"
                min="0"
                value={value}
                onInput={handleValueChange}
                onFocus={() => {
                    if (shouldClearOnFocus(value)) setValue('');
                }}
                disabled={isDisabled}
            />

            <div className="dropdown" ref={triggerRef}>
                <div
                    className={`dropdown-selected ${!dropdownIsEnabled ? 'disabled' : ''}`}
                    onClick={() => dropdownIsEnabled && setOpen((p) => !p)}
                >
                    <span className="unit-name">
                        {unit?.name}{unit?.text?.length > 0 ? ` (${unit?.text})` : ''}
                    </span>

                    {hasMultipleUnits && (
                        <span className="dropdown-icon">
                            <img src="/assets/images/diary-icons/chevron-down.svg" alt="Välj enhet" />
                        </span>
                    )}
                </div>

                {open &&
                    hasPosition &&
                    createPortal(
                        <ul
                            ref={menuRef}
                            className="dropdown-units"
                            style={menuStyle}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {availableUnits.map((opt) => (
                                <li key={opt.id} onClick={() => handleSelectUnit(opt)}>
                                    {opt.name}{opt?.text ? ` (${opt.text})` : ''}
                                </li>
                            ))}
                        </ul>,
                        document.body
                    )}
            </div>

            <button type="button" className="delete-item-btn" onClick={onDelete} disabled={isDisabled}>
                <img src="/assets/images/diary-icons/times-white.svg" alt="Radera" />
            </button>

            {invalidAmount && <span className="input-error-text">Mängden måste vara mer än noll</span>}
        </motion.div>
    );
};

export default MealItemEditor;
